import type React from 'react';

import { List as MuiList } from '@mui/material';
import {
  TimeZoneInput,
  LanguageSelectorInput,
  OrdReferenceArrayInput,
  OrdReferenceInput,
  OrdBulkUpdateFormButton,
  LabelsInput,
  OrdTabbedForm,
  useCanShowLabels,
  OrdFormList,
  useFormType,
  UserPhotoDropzone,
  OrdIconStandard,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { UserUpdateFields, dataSchema } from '@ragu-components/ord-types';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import {
  NumberInput,
  CheckboxGroupInput,
  RadioButtonGroupInput,
  TabbedForm,
  TextInput,
  useLocales,
  useLocaleState,
  useRecordContext,
  useTranslate,
  useGetIdentity,
  usePermissions,
  BooleanInput,
} from 'react-admin';

import { OrdPasswordInput } from 'src/components/formInputs';
import { useGetPasswordPolicy } from 'src/hooks/useGetPasswordPolicy';

import { AgentQueuesList } from '../agents/AgentQueuesList';

import { AclResourcesInput } from './AclResourcesInput';
import { ServiceClassesInput } from './ServiceClassesInput';
import { UserAclLabelsInput } from './UserAclLabelsInput';
import { UserAclResourcesInput } from './UserAclResourcesInput';
import { UserExtensionsTab } from './UserExtensionsTab';
import { UserMobilityTab } from './UserMobilityTab';
import { UserNumbersTab } from './UserNumbersTab';
import { UserVoicemailTab } from './UserVoicemailTab';

import type { JsonSchema, ObjectJsonSchema } from '@ragu-components/json-schema-context';
import type { OrdTabbedFormProps } from '@ragu-components/ord-components';
import type { UserRead } from '@ragu-components/ord-types';

const OrdToggleActivationAccount = (): React.ReactElement => (
  <RadioButtonGroupInput
    source={UserUpdateFields.account_disabled}
    label='resources.users.fields.account_status'
    choices={[
      { id: 'false', name: 'resources.users.fields.account_enabled' },
      { id: 'true', name: 'resources.users.fields.account_disabled' },
    ]}
    parse={(value: string) => value === 'true'}
    format={(value: boolean) => String(value ?? false)}
    helperText={false}
  />
);

export const UserForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const record = useRecordContext<UserRead>();
  const canShowLabels = useCanShowLabels();
  const [locale] = useLocaleState();
  const languages = useLocales().map(({ locale, name }) => ({ id: locale, name }));
  const translate = useTranslate();
  const { create, edit } = useFormType();
  const { isLoading, passwordPolicy } = useGetPasswordPolicy(
    edit && record !== undefined ? { serviceClassId: record?.service_class?.id } : { cosType: 'user_default' }
  );
  const { permissions } = usePermissions();
  const canShowExtensions = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.extensions,
  });
  const canShowMobility = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.mobility_configurations,
  });
  const canShowNumbers = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.user_numbers,
  });
  const canShowAgentQueues = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.agents,
  });
  const canShowVoicemails = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.voicemails,
  }); /* || // The tab visibility was also checking the edit action, but canAccessWithPermissions mathces the wildcard permission generated from ACL Labels when no record is passed (resource: '*'), so it return always true. 
  // rely on the list aciton only
    canAccessWithPermissions({
      permissions,
      action: 'edit',
      resource: Resources.voicemails,
    }); */

  // Disable editing current user's acl labels and resources
  const editingCurrentUser = record?.id === useGetIdentity()?.data?.id;

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={dataSchema.UserCreate}
      editJsonSchema={dataSchema.UserUpdate}
      transformJsonSchema={(schema: ObjectJsonSchema): ObjectJsonSchema => {
        if (schema.properties === undefined) return schema;
        const { metadata, ...rest } = schema.properties;
        const shouldAllowVoicemail = schema.$id === dataSchema.UserUpdate.$id;
        const required = schema.required?.filter((field) => field !== 'metadata');

        const canAccessOwnRecordingsSchema: JsonSchema = { type: 'boolean' };
        const metadataSchema: ObjectJsonSchema = {
          type: 'object',
          additionalProperties: true,
          properties: {
            can_access_own_recordings: canAccessOwnRecordingsSchema,
          },
        };

        if (!shouldAllowVoicemail) {
          return { ...schema, properties: { ...rest, metadata: metadataSchema }, required };
        }

        const voicemailSchema: ObjectJsonSchema = {
          type: 'object',
          $id: dataSchema.UpdateVoicemail.$id,
          additionalProperties: dataSchema.UpdateVoicemail.additionalProperties,
          properties: {
            labels: dataSchema.UpdateVoicemail.properties.labels as JsonSchema,
            preferences: {
              type: 'object',
              $id: dataSchema.VoicemailPreferences.$id,
              additionalProperties: true,
              required: ['enabled'],
              properties: {
                enabled: dataSchema.VoicemailPreferences.properties.enabled as JsonSchema,
                quota: dataSchema.VoicemailPreferences.properties.quota as JsonSchema,
              },
            },
          },
        };

        return { ...schema, properties: { ...rest, voicemail: voicemailSchema, metadata: metadataSchema }, required };
      }}
      {...props}
    >
      <TabbedForm.Tab label='resources.users.operations.sections.personalData'>
        <OrdFormList icon={<OrdIconStandard name='credentials' />}>
          <TextInput
            source={UserUpdateFields.username}
            isRequired
            resettable={create}
            disabled={edit}
            autoComplete='off'
          />
          <OrdPasswordInput
            init={false}
            isLoading={isLoading}
            isRequired={create}
            passwordPolicy={passwordPolicy}
            source={UserUpdateFields.password}
            tooltip={translate('resources.users.operations.genPassword')}
          />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='personalData' />}>
          <TextInput source={UserUpdateFields.firstname} isRequired resettable />
          <TextInput source={UserUpdateFields.middlename} resettable />
          <TextInput source={UserUpdateFields.lastname} isRequired resettable />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='email' />}>
          <TextInput source={UserUpdateFields.email} isRequired resettable />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='groups' />}>
          <OrdReferenceArrayInput resource={Resources.groups} source={UserUpdateFields.groups} />
        </OrdFormList>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.users.operations.sections.userSettings'>
        <OrdFormList icon={<OrdIconStandard name='language' />}>
          <TimeZoneInput source={UserUpdateFields.timezone} isRequired />
          <LanguageSelectorInput
            source={UserUpdateFields.language}
            choices={languages}
            defaultValue={locale}
            isRequired
          />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='userSettings' />}>
          <NumberInput
            source={UserUpdateFields.sessionexpire}
            defaultValue={3600}
            required
            helperText='resources.users.helperTexts.sessionexpire'
          />
          <ServiceClassesInput />
          <CheckboxGroupInput
            source={UserUpdateFields.required_actions}
            choices={dataSchema.UserRead.properties.required_actions.items.enum.map((action) => ({
              id: action,
              name: `resources.users.fields.required_actions_values.${action.toLowerCase()}`,
            }))}
            defaultValue={[]}
          />
          <OrdToggleActivationAccount />
          <BooleanInput
            source='metadata.can_access_own_recordings'
            label={translate('resources.users.fields.metadata.can_access_own_recordings', {
              _: 'Can access own recordings',
            })}
          />
          <BooleanInput
            source='metadata.recall_enabled'
            label={translate('resources.users.fields.metadata.recall_enabled', {
              _: 'Can use the recall service',
            })}
            helperText='resources.users.helperTexts.recall_enabled'
          />
          <BooleanInput
            source='metadata.shared_pause'
            label={translate('resources.users.fields.metadata.shared_pause')}
            helperText='resources.users.helperTexts.shared_pause'
          />
          <BooleanInput
            source='metadata.ai_voice_analysis_inbound'
            label={translate('resources.users.fields.metadata.ai_voice_analysis_inbound')}
            helperText='resources.users.helperTexts.ai_voice_analysis_inbound'
          />
          <BooleanInput
            source='metadata.ai_voice_analysis_outbound'
            label={translate('resources.users.fields.metadata.ai_voice_analysis_outbound')}
            helperText='resources.users.helperTexts.ai_voice_analysis_outbound'
          />
          <BooleanInput
            source='metadata.stats_enabled'
            label={translate('resources.users.fields.metadata.stats_enabled')}
            helperText='resources.users.helperTexts.stats_enabled'
          />
          <NumberInput
            source='metadata.identification_number'
            label={translate('resources.users.fields.metadata.identification_number')}
            helperText='resources.users.helperTexts.identification_number'
          />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='lcrs' />}>
          <OrdReferenceInput resource={Resources.assigned_lcrs} source={UserUpdateFields.assigned_lcr} />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='userMappings' />}>
          <OrdReferenceArrayInput resource={Resources.user_mappings} source={UserUpdateFields.mappings} />
        </OrdFormList>
      </TabbedForm.Tab>
      {edit && canShowExtensions && record !== undefined && (
        <TabbedForm.Tab label={translate('resources.extensions.name')}>
          <UserExtensionsTab user={record} />
        </TabbedForm.Tab>
      )}
      {edit && canShowMobility && record !== undefined && (
        <TabbedForm.Tab label={translate('resources.mobility_configurations.name')}>
          <UserMobilityTab user={record} />
        </TabbedForm.Tab>
      )}
      {edit && canShowNumbers && record !== undefined && (
        <TabbedForm.Tab label={translate('resources.user_numbers.name')}>
          <UserNumbersTab user={record} />
        </TabbedForm.Tab>
      )}
      {edit && canShowAgentQueues && record !== undefined && (
        <TabbedForm.Tab label={translate('resources.queues.name')} sx={{ height: '100%' }}>
          <AgentQueuesList filterField='user.id' filterValue={record.id} />
        </TabbedForm.Tab>
      )}
      {edit && canShowVoicemails && record !== undefined && (
        <TabbedForm.Tab label={translate('resources.voicemails.name')}>
          <UserVoicemailTab user={record} />
        </TabbedForm.Tab>
      )}
      {edit && (
        <TabbedForm.Tab label={translate('resources.users.fields.photo.name')}>
          <UserPhotoDropzone source={UserUpdateFields.photo} />
        </TabbedForm.Tab>
      )}
      {canShowLabels && (
        <TabbedForm.Tab label='resources.users.operations.sections.permissions'>
          <MuiList sx={{ width: '100%' }}>
            <OrdFormList icon={<OrdIconStandard name='security' />}>
              {edit ? (
                <>
                  <UserAclLabelsInput
                    disabled={editingCurrentUser}
                    source={UserUpdateFields.acl_labels}
                    helperText='resources.users.helperTexts.acl_labels'
                  />
                  <UserAclResourcesInput disabled={editingCurrentUser} source={UserUpdateFields.acl_resources} />
                </>
              ) : (
                <>
                  <LabelsInput disabled={editingCurrentUser} source={UserUpdateFields.acl_labels} />
                  <AclResourcesInput disabled={editingCurrentUser} source={UserUpdateFields.acl_resources} />
                </>
              )}
            </OrdFormList>
          </MuiList>
        </TabbedForm.Tab>
      )}
    </OrdTabbedForm>
  );
};

/** Form for bulk editing users */
export const UsersBulkEditForm = (): React.ReactElement => {
  const [locale] = useLocaleState();
  const languages = useLocales().map(({ locale, name }) => ({ id: locale, name }));

  const inputs = [
    {
      label: 'resources.users.fields.groups.name',
      element: (
        <OrdFormList icon={<OrdIconStandard name='groups' />}>
          <OrdReferenceArrayInput resource={Resources.groups} source={UserUpdateFields.groups} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.timezone',
      element: (
        <OrdFormList icon={<OrdIconStandard name='language' />}>
          <TimeZoneInput source={UserUpdateFields.timezone} isRequired />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.language',
      element: (
        <OrdFormList icon={<OrdIconStandard name='language' />}>
          <LanguageSelectorInput source={UserUpdateFields.language} choices={languages} defaultValue={locale} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.sessionexpire',
      element: (
        <OrdFormList icon={<OrdIconStandard name='timer' />}>
          <NumberInput
            source={UserUpdateFields.sessionexpire}
            defaultValue={3600}
            helperText='resources.users.helperTexts.sessionexpire'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.labels.name',
      element: (
        <OrdFormList icon={<OrdIconStandard name='security' />}>
          <LabelsInput />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.acl_labels.bulkUpdateLabel',
      element: (
        <OrdFormList icon={<OrdIconStandard name='security' />}>
          <UserAclLabelsInput source={UserUpdateFields.acl_labels} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.acl_resources',
      element: (
        <OrdFormList icon={<OrdIconStandard name='security' />}>
          <UserAclResourcesInput source={UserUpdateFields.acl_resources} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.mappings',
      element: (
        <OrdFormList icon={<OrdIconStandard name='userMappings' />}>
          <OrdReferenceArrayInput resource={Resources.user_mappings} source={UserUpdateFields.mappings} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.mobility_configuration',
      element: (
        <OrdFormList icon={<OrdIconStandard name='mobilityConfigurations' />}>
          <OrdReferenceInput
            resource={Resources.mobility_configurations}
            source={UserUpdateFields.mobility_configuration}
            label='resources.users.fields.mobility_configuration'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.users.fields.enabling',
      element: (
        <OrdFormList icon={<OrdIconStandard name='mobilityConfigurations' />}>
          <OrdToggleActivationAccount />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
